/**
 * Prioritisation, ported verbatim from the production app
 * (BetterStep/src/shared/config/addiction-severity.ts — CH01-D-PRIORITY-FORMULA-V2
 * with the CH01-D-PRIORITY-RECENCY-90D override).
 *
 *   P = lifeImpactBase × state
 *   state = ACTIVE   → freqMult × controlMult
 *           RECOVERY → recencyMult(daysClean)
 *
 * Higher score = the area the program should focus on first.
 */
import { AREA_BY_SLUG } from '../data/onboarding'

/** Life-impact base 1–10. Mid behavioural (4) when a slug is unknown. */
export function lifeImpactBase(slug) {
  return AREA_BY_SLUG[slug]?.lifeImpactBase ?? 4
}

// Frequency multiplier: the stored 1.00–1.30 option value maps to an intensity
// f ∈ [0,1], then freq_mult = 0.30 + 1.70·f. Missing → worst plausible.
function freqMult(v) {
  const f = v == null ? 0.75 : v >= 1.3 ? 0.75 : v >= 1.15 ? 0.5 : v >= 1.05 ? 0.4 : 0.15
  return 0.3 + 1.7 * f
}

// Control multiplier 0.92–1.15. Floored so "I can stop easily" never erases a
// dangerous frequency.
function controlMult(v) {
  return v == null ? 1.15 : v >= 1.3 ? 1.15 : v >= 1.15 ? 1.08 : v >= 1.05 ? 1.0 : 0.92
}

// Recency multiplier by days clean, linearly interpolated. The first ~90 days
// stay heavy (the acute relapse window), then a recovery fades toward the floor.
const RECENCY_POINTS = [
  [0, 1.4],
  [14, 1.15],
  [30, 1.0],
  [90, 0.8],
  [180, 0.6],
  [365, 0.45],
  [730, 0.35],
  [1825, 0.3],
]

export function recencyMult(daysClean) {
  if (daysClean <= 0) return 1.4
  if (daysClean >= 1825) return 0.3
  for (let i = 1; i < RECENCY_POINTS.length; i++) {
    const [d0, m0] = RECENCY_POINTS[i - 1]
    const [d1, m1] = RECENCY_POINTS[i]
    if (daysClean <= d1) return m0 + ((daysClean - d0) / (d1 - d0)) * (m1 - m0)
  }
  return 0.3
}

const round1 = (n) => Math.round(n * 10) / 10

/**
 * @param {string} slug
 * @param {{status?: 'active'|'recovery', frequency?: number, control?: number, daysClean?: number}} gate
 */
export function priorityScore(slug, gate) {
  const base = lifeImpactBase(slug)
  if (!gate?.status) return base
  if (gate.status === 'recovery') return round1(base * recencyMult(gate.daysClean ?? 0))
  return round1(base * freqMult(gate.frequency) * controlMult(gate.control))
}

/** Slugs ordered by priority score, highest first (stable for ties). */
export function sortByPriority(slugs, gateOf) {
  return slugs
    .map((slug, idx) => ({ slug, idx, score: priorityScore(slug, gateOf(slug)) }))
    .sort((a, b) => b.score - a.score || a.idx - b.idx)
    .map((e) => e.slug)
}
